import { useEffect, useRef, useState } from 'react'
import { Menu } from 'lucide-react'
import { matchPath, useLocation } from 'react-router-dom'
import { getLocalizedPath, localizedRoutes, supportedLanguages, type RouteKey, type SupportedLanguage } from '../i18n/routes'
import { useI18n } from '../i18n/I18nProvider'
import { Link } from '../ui/Link'
import { Button } from '../ui/Button'
import { Sheet } from '../ui/Sheet'
import { Brand } from './Brand'
import type { UiMessageKey } from '../i18n/ui'

const navigation: ReadonlyArray<{ route: RouteKey; label: UiMessageKey }> = [
  { route: 'vehicles', label: 'nav.vehicles' },
  { route: 'services', label: 'nav.services' },
  { route: 'sellVehicle', label: 'nav.sellVehicle' },
  { route: 'contacts', label: 'nav.contacts' },
]

const languageLabels: Record<SupportedLanguage, { full: UiMessageKey; short: UiMessageKey }> = {
  it: { full: 'shell.languageItalian', short: 'shell.languageItalianShort' },
  en: { full: 'shell.languageEnglish', short: 'shell.languageEnglishShort' },
}

function getEquivalentLanguagePath(pathname: string, search: string, currentLanguage: SupportedLanguage, targetLanguage: SupportedLanguage) {
  for (const route of Object.keys(localizedRoutes) as RouteKey[]) {
    const segment = localizedRoutes[route][currentLanguage]
    const currentPattern = segment ? `/${currentLanguage}/${segment}` : `/${currentLanguage}`
    const match = matchPath({ path: currentPattern, end: true }, pathname)
    if (match) return `${getLocalizedPath(route, targetLanguage, match.params as Record<string, string>)}${search}`
  }
  return `${getLocalizedPath('home', targetLanguage)}${search}`
}

export function Header() {
  const { language, t } = useI18n()
  const location = useLocation()
  const [isCompact, setIsCompact] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const isHome = location.pathname === getLocalizedPath('home', language)

  useEffect(() => {
    const updateHeader = () => setIsCompact(window.scrollY > 24)
    updateHeader()
    window.addEventListener('scroll', updateHeader, { passive: true })
    return () => window.removeEventListener('scroll', updateHeader)
  }, [])

  useEffect(() => setMenuOpen(false), [location.pathname])

  const isCurrent = (route: RouteKey) => {
    const path = getLocalizedPath(route, language)
    return route === 'home' ? location.pathname === path : location.pathname.startsWith(path)
  }

  const languageSelector = (className: string) => (
    <nav className={className} aria-label={t('shell.languageSelectorLabel')}>
      {supportedLanguages.map((targetLanguage) => (
        <Link
          key={targetLanguage}
          to={getEquivalentLanguagePath(location.pathname, location.search, language, targetLanguage)}
          aria-label={t(languageLabels[targetLanguage].full)}
          aria-current={targetLanguage === language ? 'page' : undefined}
        >
          {t(languageLabels[targetLanguage].short)}
        </Link>
      ))}
    </nav>
  )

  return (
    <header className={`site-header${isHome ? ' is-home-overlay' : ''}${!isCompact ? ' is-at-top' : ''}${isCompact ? ' is-compact' : ''}`}>
      <div className="site-header__inner page-frame">
        <Link className="site-header__brand" to={getLocalizedPath('home', language)} aria-label={t('brand.homeLabel')}>
          <Brand />
        </Link>

        <nav className="desktop-nav" aria-label={t('shell.menuTitle')}>
          {navigation.map((item) => (
            <Link
              key={item.route}
              className="desktop-nav__link"
              to={getLocalizedPath(item.route, language)}
              aria-current={isCurrent(item.route) ? 'page' : undefined}
            >
              {t(item.label)}
            </Link>
          ))}
        </nav>

        <div className="site-header__actions">
          {languageSelector('language-selector language-selector--desktop')}
          <Button
            ref={menuButtonRef}
            className="menu-trigger"
            variant="secondary"
            size="icon"
            aria-label={t('common.openMenu')}
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen(true)}
          >
            <Menu aria-hidden="true" />
          </Button>
        </div>
      </div>

      <Sheet
        open={menuOpen}
        onOpenChange={setMenuOpen}
        title={t('shell.menuTitle')}
        description={t('shell.menuDescription')}
        closeLabel={t('common.closeMenu')}
        triggerRef={menuButtonRef}
        className="mobile-menu"
      >
        <nav id="mobile-navigation" className="mobile-nav" aria-label={t('shell.menuTitle')}>
          <Link
            className="mobile-nav__link"
            to={getLocalizedPath('home', language)}
            aria-current={isCurrent('home') ? 'page' : undefined}
          >
            {t('nav.home')}
          </Link>
          {navigation.map((item) => (
            <Link
              key={item.route}
              className="mobile-nav__link"
              to={getLocalizedPath(item.route, language)}
              aria-current={isCurrent(item.route) ? 'page' : undefined}
            >
              {t(item.label)}
            </Link>
          ))}
        </nav>
        {languageSelector('language-selector language-selector--mobile')}
        <p className="mobile-menu__locale">{t('shell.draftLanguage')}</p>
      </Sheet>
    </header>
  )
}
